import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Shield, AlertTriangle, CheckCircle, Loader2, 
  User, MapPin, DollarSign, Send, CreditCard,
  Eye, Lock, Smartphone, ArrowRight, XCircle,
  FileText, TrendingUp, Wallet, ExternalLink,
  Building, Globe, Banknote, Clock, Scale
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProtectPage = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('screen');
  const [screeningResult, setScreeningResult] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [report, setReport] = useState(null);
  const [showSelleCheckForm, setShowSelleCheckForm] = useState(false);
  const [showEscrowForm, setShowEscrowForm] = useState(false);
  const [escrowCalculation, setEscrowCalculation] = useState(null);
  const { getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // SelleCheck form data
  const [selleCheckData, setSelleCheckData] = useState({
    account_holder_name: '',
    account_type: 'checking',
    routing_number: '',
    account_number: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip: ''
  });

  // Escrow form data
  const [escrowData, setEscrowData] = useState({
    escrow_type: 'standard',
    escrow_days: 14,
    release_conditions: '',
    international_transfer: false,
    recipient_country: ''
  });

  const [formData, setFormData] = useState({
    // Shipper
    shipper_name: '',
    shipper_email: '',
    shipper_phone: '',
    shipper_address: '',
    shipper_city: '',
    shipper_state: '',
    shipper_zip: '',
    // Recipient
    recipient_name: '',
    recipient_email: '',
    recipient_phone: '',
    recipient_address: '',
    recipient_city: '',
    recipient_state: '',
    recipient_zip: '',
    // Transaction
    transaction_amount: '',
    description: '',
    require_buyer_consent: true,
    use_3d_secure: true,
  });

  useEffect(() => {
    fetchSessions();
    fetchReport();
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    const protectId = searchParams.get('protect_id');
    if (sessionId && protectId) {
      pollPaymentStatus(sessionId, protectId);
    }
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/protect/sessions`, {
        headers: getAuthHeaders()
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/protect`, {
        headers: getAuthHeaders()
      });
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, protectId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out');
      return;
    }

    try {
      const response = await axios.get(`${API}/protect/checkout/status/${sessionId}`, {
        headers: getAuthHeaders()
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Transaction protected.');
        fetchSessions();
        fetchReport();
        setActiveTab('history');
        navigate('/dashboard/protect', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, protectId, attempts + 1), 2000);
    } catch (error) {
      console.error('Payment status error:', error);
    }
  };

  const runScreening = async () => {
    if (!formData.recipient_email || !formData.transaction_amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/protect/screen`, {
        ...formData,
        transaction_amount: parseFloat(formData.transaction_amount)
      }, {
        headers: getAuthHeaders()
      });

      setScreeningResult(response.data);
      toast.success('Fraud screening complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Screening failed');
    } finally {
      setLoading(false);
    }
  };

  const sendVerification = async () => {
    if (!screeningResult?.session_id) return;

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/protect/send-verification/${screeningResult.session_id}`,
        {},
        { headers: getAuthHeaders() }
      );

      toast.success('Verification sent to buyer');
      setScreeningResult({
        ...screeningResult,
        verification_sent: true,
        verification_code: response.data.verification_code
      });
    } catch (error) {
      toast.error('Failed to send verification');
    } finally {
      setLoading(false);
    }
  };

  const proceedToPayment = async () => {
    if (!screeningResult?.session_id) return;

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/protect/checkout/${screeningResult.session_id}`,
        {
          protect_session_id: screeningResult.session_id,
          origin_url: window.location.origin,
          use_3d_secure: formData.use_3d_secure
        },
        { headers: getAuthHeaders() }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout');
      setLoading(false);
    }
  };

  // SelleCheck eCheck payment
  const initiateSelleCheck = async () => {
    if (!screeningResult?.session_id) return;
    
    // Validate form
    if (!selleCheckData.account_holder_name || !selleCheckData.routing_number || !selleCheckData.account_number) {
      toast.error('Please fill in all bank account information');
      return;
    }

    if (selleCheckData.routing_number.length !== 9) {
      toast.error('Routing number must be 9 digits');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/sellecheck/initiate`,
        {
          protect_session_id: screeningResult.session_id,
          ...selleCheckData
        },
        { headers: getAuthHeaders() }
      );

      toast.success('eCheck initiated! Micro-deposits will arrive in 1-2 days.');
      setScreeningResult({
        ...screeningResult,
        sellecheck_transaction_id: response.data.transaction_id,
        payment_method: 'sellecheck'
      });
      setShowSelleCheckForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate eCheck');
    } finally {
      setLoading(false);
    }
  };

  // Calculate Escrow fees
  const calculateEscrowFees = async () => {
    if (!screeningResult?.session_id) return;

    try {
      const response = await axios.post(
        `${API}/transact-escrow/calculate?protect_session_id=${screeningResult.session_id}`,
        {},
        { headers: getAuthHeaders() }
      );

      setEscrowCalculation(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate escrow fees');
    }
  };

  // Initiate Transact Escrow
  const initiateEscrow = async () => {
    if (!screeningResult?.session_id) return;

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/transact-escrow/initiate`,
        {
          protect_session_id: screeningResult.session_id,
          ...escrowData
        },
        { headers: getAuthHeaders() }
      );

      toast.success('Escrow initiated! Proceed to fund the escrow.');
      
      // Redirect to fund escrow
      const fundResponse = await axios.post(
        `${API}/transact-escrow/fund/${response.data.escrow_id}`,
        {},
        { headers: getAuthHeaders() }
      );

      window.location.href = fundResponse.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate escrow');
      setLoading(false);
    }
  };

  // Load escrow fees when form opens
  useEffect(() => {
    if (showEscrowForm && screeningResult?.session_id) {
      calculateEscrowFees();
    }
  }, [showEscrowForm]);

  const getTrustScoreColor = (score) => {
    if (score >= 0.7) return 'text-emerald-600';
    if (score >= 0.4) return 'text-amber-600';
    return 'text-red-600';
  };

  const getRiskBadgeColor = (level) => {
    if (level === 'low') return 'bg-emerald-500';
    if (level === 'medium') return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="protect-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            SelleSend Protect
          </h1>
          <p className="text-slate-500 mt-1">Advanced fraud screening & transaction verification</p>
        </div>
        {report && (
          <div className="flex gap-4">
            <div className="text-right">
              <p className="text-2xl font-bold tabular-nums">{report.summary?.total_screened || 0}</p>
              <p className="text-xs text-slate-500">Transactions Screened</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold tabular-nums text-emerald-600">
                ${report.summary?.total_fees_charged?.toFixed(2) || '0.00'}
              </p>
              <p className="text-xs text-slate-500">Fees Collected</p>
            </div>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="screen" data-testid="tab-screen">
            <Shield className="w-4 h-4 mr-2" />
            Screen Transaction
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <FileText className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">
            <TrendingUp className="w-4 h-4 mr-2" />
            Reports
          </TabsTrigger>
        </TabsList>

        {/* Screen Transaction Tab */}
        <TabsContent value="screen" className="space-y-6">
          {!screeningResult ? (
            <>
              {/* Shipper Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-500" />
                    Shipper Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={formData.shipper_name}
                        onChange={(e) => setFormData({...formData, shipper_name: e.target.value})}
                        placeholder="Full name"
                        data-testid="shipper-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.shipper_email}
                        onChange={(e) => setFormData({...formData, shipper_email: e.target.value})}
                        placeholder="email@company.com"
                        data-testid="shipper-email"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2 col-span-2">
                      <Label>Address</Label>
                      <Input
                        value={formData.shipper_address}
                        onChange={(e) => setFormData({...formData, shipper_address: e.target.value})}
                        placeholder="Street address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={formData.shipper_phone}
                        onChange={(e) => setFormData({...formData, shipper_phone: e.target.value})}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        value={formData.shipper_city}
                        onChange={(e) => setFormData({...formData, shipper_city: e.target.value})}
                        placeholder="City"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Input
                        value={formData.shipper_state}
                        onChange={(e) => setFormData({...formData, shipper_state: e.target.value})}
                        placeholder="CA"
                        maxLength={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>ZIP</Label>
                      <Input
                        value={formData.shipper_zip}
                        onChange={(e) => setFormData({...formData, shipper_zip: e.target.value})}
                        placeholder="90001"
                        maxLength={5}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recipient Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-purple-500" />
                    Recipient Information
                  </CardTitle>
                  <CardDescription>
                    This information will be verified against fraud databases
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={formData.recipient_name}
                        onChange={(e) => setFormData({...formData, recipient_name: e.target.value})}
                        placeholder="Full name"
                        data-testid="recipient-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.recipient_email}
                        onChange={(e) => setFormData({...formData, recipient_email: e.target.value})}
                        placeholder="buyer@email.com"
                        data-testid="recipient-email"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2 col-span-2">
                      <Label>Address *</Label>
                      <Input
                        value={formData.recipient_address}
                        onChange={(e) => setFormData({...formData, recipient_address: e.target.value})}
                        placeholder="Street address"
                        data-testid="recipient-address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={formData.recipient_phone}
                        onChange={(e) => setFormData({...formData, recipient_phone: e.target.value})}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>City *</Label>
                      <Input
                        value={formData.recipient_city}
                        onChange={(e) => setFormData({...formData, recipient_city: e.target.value})}
                        placeholder="City"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State *</Label>
                      <Input
                        value={formData.recipient_state}
                        onChange={(e) => setFormData({...formData, recipient_state: e.target.value})}
                        placeholder="NY"
                        maxLength={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>ZIP *</Label>
                      <Input
                        value={formData.recipient_zip}
                        onChange={(e) => setFormData({...formData, recipient_zip: e.target.value})}
                        placeholder="10001"
                        maxLength={5}
                        data-testid="recipient-zip"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Transaction Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-500" />
                    Transaction Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Transaction Amount ($) *</Label>
                      <Input
                        type="number"
                        value={formData.transaction_amount}
                        onChange={(e) => setFormData({...formData, transaction_amount: e.target.value})}
                        placeholder="0.00"
                        min="1"
                        step="0.01"
                        data-testid="transaction-amount"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        placeholder="Item or service being sold"
                        data-testid="description"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-6 pt-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="require_buyer_consent"
                        checked={formData.require_buyer_consent}
                        onCheckedChange={(checked) => setFormData({...formData, require_buyer_consent: checked})}
                      />
                      <Label htmlFor="require_buyer_consent" className="cursor-pointer flex items-center gap-1">
                        <Smartphone className="w-4 h-4" />
                        Require Buyer Verification (+$1.49)
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="use_3d_secure"
                        checked={formData.use_3d_secure}
                        onCheckedChange={(checked) => setFormData({...formData, use_3d_secure: checked})}
                      />
                      <Label htmlFor="use_3d_secure" className="cursor-pointer flex items-center gap-1">
                        <Lock className="w-4 h-4" />
                        Enable 3D Secure (Free)
                      </Label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Fee Preview */}
              <Card className="bg-slate-50 dark:bg-slate-900">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium">Estimated Fees</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold tabular-nums">
                        ${(4.99 + (formData.require_buyer_consent ? 1.49 : 0) + (parseFloat(formData.transaction_amount || 0) * 0.045)).toFixed(2)}
                      </span>
                      <p className="text-xs text-slate-500">+ transaction amount</p>
                    </div>
                  </div>
                  <div className="text-sm text-slate-500 space-y-1">
                    <div className="flex justify-between">
                      <span>Fraud Screening:</span>
                      <span>$4.99</span>
                    </div>
                    {formData.require_buyer_consent && (
                      <div className="flex justify-between">
                        <span>Buyer Verification:</span>
                        <span>$1.49</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Platform Fee (4.5%):</span>
                      <span>${(parseFloat(formData.transaction_amount || 0) * 0.045).toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button 
                onClick={runScreening} 
                className="w-full btn-press" 
                size="lg"
                disabled={loading}
                data-testid="run-screening-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Run Fraud Screening
                  </>
                )}
              </Button>
            </>
          ) : (
            /* Screening Results */
            <div className="space-y-6">
              {/* Trust Score Card */}
              <Card className={`border-2 ${
                screeningResult.risk_level === 'low' ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10' :
                screeningResult.risk_level === 'medium' ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-900/10' :
                'border-red-500 bg-red-50/50 dark:bg-red-900/10'
              }`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium">Trust Score</h3>
                      <Badge className={`${getRiskBadgeColor(screeningResult.risk_level)} text-white mt-1`}>
                        {screeningResult.risk_level.toUpperCase()} RISK
                      </Badge>
                    </div>
                    <div className="text-right">
                      <span className={`text-5xl font-bold tabular-nums ${getTrustScoreColor(screeningResult.trust_score)}`}>
                        {Math.round(screeningResult.trust_score * 100)}
                      </span>
                      <span className="text-xl text-slate-400">/100</span>
                    </div>
                  </div>
                  
                  <Progress 
                    value={screeningResult.trust_score * 100} 
                    className="h-3"
                  />

                  <div className="mt-4 p-3 rounded-lg bg-white/50 dark:bg-slate-900/50">
                    <p className={`font-medium ${screeningResult.should_proceed ? 'text-emerald-600' : 'text-red-600'}`}>
                      {screeningResult.should_proceed ? (
                        <><CheckCircle className="w-4 h-4 inline mr-2" />Transaction can proceed</>
                      ) : (
                        <><XCircle className="w-4 h-4 inline mr-2" />Transaction flagged - manual review required</>
                      )}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">{screeningResult.recommendation}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Risk Factors */}
              {screeningResult.risk_factors?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Risk Factors Detected
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {screeningResult.risk_factors.map((factor, index) => (
                        <li key={index} className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                          {factor}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Verification & Payment Actions */}
              {screeningResult.should_proceed && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-blue-500" />
                      Choose Payment Method
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {screeningResult.requires_verification && !screeningResult.verification_sent && (
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="font-medium text-blue-700 dark:text-blue-300 mb-2">
                          Buyer Verification Required
                        </p>
                        <p className="text-sm text-blue-600 dark:text-blue-400 mb-3">
                          Send a verification request to the buyer for additional fraud protection.
                        </p>
                        <Button onClick={sendVerification} disabled={loading} variant="outline">
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Verification to Buyer
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {screeningResult.verification_sent && (
                      <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                        <p className="font-medium text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Verification Sent
                        </p>
                        <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
                          Code: <code className="bg-emerald-100 dark:bg-emerald-900 px-2 py-1 rounded">{screeningResult.verification_code}</code>
                        </p>
                      </div>
                    )}

                    {/* Fee Summary */}
                    <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      <h4 className="font-medium mb-3">Fee Summary</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Fraud Screening:</span>
                          <span>${screeningResult.fees?.fraud_screening?.toFixed(2)}</span>
                        </div>
                        {screeningResult.fees?.transaction_verification > 0 && (
                          <div className="flex justify-between">
                            <span>Transaction Verification:</span>
                            <span>${screeningResult.fees?.transaction_verification?.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span>Platform Fee (4.5%):</span>
                          <span>${screeningResult.fees?.platform_fee?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between border-t pt-2 font-semibold">
                          <span>Total Fees:</span>
                          <span>${screeningResult.fees?.total?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold pt-2 border-t">
                          <span>Grand Total:</span>
                          <span className="text-blue-600">
                            ${(parseFloat(formData.transaction_amount) + screeningResult.fees?.total).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div className="space-y-3">
                      <h4 className="font-medium">Select Payment Method</h4>
                      
                      {/* Credit Card with 3D Secure */}
                      <div 
                        className="p-4 border-2 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-white dark:bg-slate-950"
                        onClick={proceedToPayment}
                        data-testid="pay-card-btn"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                              <CreditCard className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">Credit/Debit Card</p>
                              <p className="text-sm text-slate-500">Visa, Mastercard, Amex with 3D Secure</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-emerald-600 border-emerald-600">Instant</Badge>
                        </div>
                      </div>

                      {/* SelleCheck eCheck */}
                      <div 
                        className="p-4 border-2 rounded-lg cursor-pointer hover:border-purple-500 transition-colors bg-white dark:bg-slate-950"
                        onClick={() => setShowSelleCheckForm(true)}
                        data-testid="pay-sellecheck-btn"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                              <Building className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium">SelleCheck eCheck</p>
                              <p className="text-sm text-slate-500">Pay from bank account • 1.5% + $1.50</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-purple-600 border-purple-600">Low Fee</Badge>
                        </div>
                      </div>

                      {/* Transact Escrow */}
                      <div 
                        className="p-4 border-2 rounded-lg cursor-pointer hover:border-amber-500 transition-colors bg-white dark:bg-slate-950"
                        onClick={() => setShowEscrowForm(true)}
                        data-testid="pay-escrow-btn"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                              <Globe className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium">Transact Escrow</p>
                              <p className="text-sm text-slate-500">International • 1.99% (min $10)</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-amber-600 border-amber-600">Licensed</Badge>
                        </div>
                        <p className="text-xs text-slate-400 mt-2 ml-13">
                          $10 - $50,000 • Fully licensed escrow service
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-slate-500 text-center">
                      All payments are secure and protected by SelleSend
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* SelleCheck eCheck Form */}
              {showSelleCheckForm && screeningResult?.should_proceed && (
                <Card className="border-2 border-purple-500">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Building className="w-5 h-5 text-purple-500" />
                        SelleCheck eCheck Payment
                      </CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setShowSelleCheckForm(false)}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                    <CardDescription>
                      Pay directly from your bank account with lower fees
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-sm">
                      <p className="font-medium text-purple-700 dark:text-purple-300">Fee: 1.5% + $1.50</p>
                      <p className="text-purple-600 dark:text-purple-400">Processing time: 3-5 business days</p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Account Holder Name *</Label>
                        <Input
                          value={selleCheckData.account_holder_name}
                          onChange={(e) => setSelleCheckData({...selleCheckData, account_holder_name: e.target.value})}
                          placeholder="John Doe"
                          data-testid="sellecheck-holder-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Account Type *</Label>
                        <select
                          className="w-full p-2 border rounded-md bg-background"
                          value={selleCheckData.account_type}
                          onChange={(e) => setSelleCheckData({...selleCheckData, account_type: e.target.value})}
                        >
                          <option value="checking">Checking</option>
                          <option value="savings">Savings</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Routing Number *</Label>
                        <Input
                          value={selleCheckData.routing_number}
                          onChange={(e) => setSelleCheckData({...selleCheckData, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9)})}
                          placeholder="123456789"
                          maxLength={9}
                          data-testid="sellecheck-routing"
                        />
                        <p className="text-xs text-slate-500">9 digits</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Account Number *</Label>
                        <Input
                          type="password"
                          value={selleCheckData.account_number}
                          onChange={(e) => setSelleCheckData({...selleCheckData, account_number: e.target.value.replace(/\D/g, '')})}
                          placeholder="••••••••••"
                          data-testid="sellecheck-account"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Billing Address *</Label>
                      <Input
                        value={selleCheckData.billing_address}
                        onChange={(e) => setSelleCheckData({...selleCheckData, billing_address: e.target.value})}
                        placeholder="123 Main St"
                      />
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>City *</Label>
                        <Input
                          value={selleCheckData.billing_city}
                          onChange={(e) => setSelleCheckData({...selleCheckData, billing_city: e.target.value})}
                          placeholder="New York"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>State *</Label>
                        <Input
                          value={selleCheckData.billing_state}
                          onChange={(e) => setSelleCheckData({...selleCheckData, billing_state: e.target.value.toUpperCase()})}
                          placeholder="NY"
                          maxLength={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>ZIP *</Label>
                        <Input
                          value={selleCheckData.billing_zip}
                          onChange={(e) => setSelleCheckData({...selleCheckData, billing_zip: e.target.value})}
                          placeholder="10001"
                          maxLength={5}
                        />
                      </div>
                    </div>

                    <Button 
                      onClick={initiateSelleCheck} 
                      className="w-full bg-purple-600 hover:bg-purple-700" 
                      disabled={loading}
                      data-testid="submit-sellecheck-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <>
                          <Banknote className="w-4 h-4 mr-2" />
                          Initiate eCheck Payment
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Transact Escrow Form */}
              {showEscrowForm && screeningResult?.should_proceed && (
                <Card className="border-2 border-amber-500">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Scale className="w-5 h-5 text-amber-500" />
                        Transact Escrow
                      </CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setShowEscrowForm(false)}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                    <CardDescription>
                      Secure international escrow service • Licensed & Compliant
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-amber-700 dark:text-amber-300">Fully Licensed Escrow Company</span>
                      </div>
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        Fee: 1.99% (minimum $10.00) • Range: $10 - $50,000
                      </p>
                    </div>

                    {escrowCalculation && (
                      <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg space-y-2">
                        <h4 className="font-medium">Escrow Fee Calculation</h4>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span>Transaction + Protect Fees:</span>
                            <span>${escrowCalculation.subtotal?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Escrow Fee (1.99%):</span>
                            <span>${escrowCalculation.escrow_fee?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-bold border-t pt-2">
                            <span>Total with Escrow:</span>
                            <span className="text-amber-600">${escrowCalculation.grand_total?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Escrow Type *</Label>
                        <select
                          className="w-full p-2 border rounded-md bg-background"
                          value={escrowData.escrow_type}
                          onChange={(e) => setEscrowData({...escrowData, escrow_type: e.target.value})}
                        >
                          <option value="standard">Standard (Time-based)</option>
                          <option value="milestone">Milestone Release</option>
                          <option value="inspection">Buyer Inspection</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Hold Period (days) *</Label>
                        <Input
                          type="number"
                          value={escrowData.escrow_days}
                          onChange={(e) => setEscrowData({...escrowData, escrow_days: parseInt(e.target.value) || 14})}
                          min={1}
                          max={90}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="international_transfer"
                        checked={escrowData.international_transfer}
                        onCheckedChange={(checked) => setEscrowData({...escrowData, international_transfer: checked})}
                      />
                      <Label htmlFor="international_transfer" className="cursor-pointer flex items-center gap-1">
                        <Globe className="w-4 h-4" />
                        International Transfer
                      </Label>
                    </div>

                    {escrowData.international_transfer && (
                      <div className="space-y-2">
                        <Label>Recipient Country *</Label>
                        <Input
                          value={escrowData.recipient_country}
                          onChange={(e) => setEscrowData({...escrowData, recipient_country: e.target.value})}
                          placeholder="e.g., UK, Canada, Australia"
                        />
                        <p className="text-xs text-slate-500">Supported: US, EU, UK, CA, AU</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Release Conditions (Optional)</Label>
                      <Textarea
                        value={escrowData.release_conditions}
                        onChange={(e) => setEscrowData({...escrowData, release_conditions: e.target.value})}
                        placeholder="e.g., Upon delivery confirmation and inspection"
                        rows={2}
                      />
                    </div>

                    <Button 
                      onClick={initiateEscrow} 
                      className="w-full bg-amber-600 hover:bg-amber-700" 
                      disabled={loading}
                      data-testid="submit-escrow-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <>
                          <Scale className="w-4 h-4 mr-2" />
                          Initiate Escrow (Pay ${escrowCalculation?.grand_total?.toFixed(2) || '...'})
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-slate-500 text-center">
                      By proceeding, you agree to Transact Escrow's terms and conditions
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Start Over */}
              <Button 
                variant="outline" 
                onClick={() => setScreeningResult(null)}
                className="w-full"
              >
                Start New Screening
              </Button>
            </div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
              <CardDescription>All SelleSend Protect screenings</CardDescription>
            </CardHeader>
            <CardContent>
              {sessions.length > 0 ? (
                <div className="space-y-4">
                  {sessions.map((session, index) => (
                    <div 
                      key={index} 
                      className="p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Badge className={`${getRiskBadgeColor(session.risk_level)} text-white`}>
                            {session.risk_level?.toUpperCase()}
                          </Badge>
                          <span className="font-medium">{session.recipient?.name}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold tabular-nums">${session.transaction_amount?.toFixed(2)}</p>
                          <Badge variant={session.payment_status === 'paid' ? 'default' : 'secondary'}>
                            {session.payment_status || 'pending'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>{session.description}</span>
                        <span>Trust: {Math.round((session.trust_score || 0) * 100)}%</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(session.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No screening history yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => setActiveTab('screen')}>
                    Run Your First Screening
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          {report && (
            <>
              {/* Summary Cards */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                        <Eye className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold tabular-nums">{report.summary?.total_screened || 0}</p>
                        <p className="text-xs text-slate-500">Total Screened</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold tabular-nums">${report.summary?.total_transaction_value?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-slate-500">Transaction Value</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold tabular-nums">${report.summary?.total_fees_charged?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-slate-500">Fees Collected</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                        <XCircle className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold tabular-nums">{report.summary?.blocked_transactions || 0}</p>
                        <p className="text-xs text-slate-500">Blocked</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Risk Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Risk Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                      <p className="text-3xl font-bold text-emerald-600">{report.summary?.risk_breakdown?.low || 0}</p>
                      <p className="text-sm text-emerald-700 dark:text-emerald-300">Low Risk</p>
                    </div>
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                      <p className="text-3xl font-bold text-amber-600">{report.summary?.risk_breakdown?.medium || 0}</p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">Medium Risk</p>
                    </div>
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                      <p className="text-3xl font-bold text-red-600">{report.summary?.risk_breakdown?.high || 0}</p>
                      <p className="text-sm text-red-700 dark:text-red-300">High Risk</p>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Average Trust Score</span>
                      <span className="text-2xl font-bold tabular-nums">
                        {Math.round((report.summary?.average_trust_score || 0) * 100)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* External Link */}
              <Card className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">SelleSafe Dashboard</h3>
                      <p className="text-sm text-slate-500">View detailed analytics and manage verifications</p>
                    </div>
                    <Button variant="outline" asChild>
                      <a href="https://sellesafe.smartgstx.com" target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open SelleSafe
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProtectPage;
